/**
 * Validation engine fixes (policy batch 2, item 2) — THE predicate for "does
 * this sequence row still occupy its sequence number?", shared by the create /
 * import collision guards, the validator's family counting, lifecycle history
 * and the New Submission wizard (via `@shared/sequenceSlot`).
 *
 * The route used to free every row whose Status was 'Rejected' while the
 * validator counted the same row, so a "freed" number was then reported as a
 * duplicate. More importantly, `Status = 'Rejected'` alone does not say WHY:
 * the status ladder (`submissionStatus.ts deriveFullStatus`) produces it from a
 * gateway acknowledgement, but the value cannot distinguish a technical
 * transmission rejection from a substantive one, and neither a bare 'Rejected'
 * nor a 'Withdrawn' row proves the agency never received the sequence.
 *
 * Sources (outputs/validation-engine-fixes/research/parent-policy-evidence.md):
 *   - FDA eCTD Validation Criteria v4.7: High severity = "considered not
 *     received by FDA"; criterion 1697 rejects a duplicate of a sequence
 *     PREVIOUSLY SUBMITTED (i.e. received).
 *   - EU Harmonised Technical Guidance 6.0.1 §2.9.4: a technically invalid
 *     sequence is resubmitted under the same number unless EMA/NCA says
 *     otherwise; content or regulatory updates require a new number.
 *
 * So a number is freed ONLY by positive technical-rejection evidence: the
 * row's Status is 'Rejected' AND a gateway acknowledgement was received
 * (`EsgAckReceivedAt`) AND that acknowledgement's recorded payload is the ESG
 * monitor's rejection shape (`EsgAckPayload.kind === 'rejected'`, written only
 * by `esgMonitorService.applyRejection` from the gateway client's
 * `{ kind: 'rejected' }` lookup — "ESG rejected the submission (validation
 * failure, duplicate submission, etc.)"). Everything else keeps its slot.
 *
 * Pure (no database or runtime imports) so the browser can share it.
 */

/** The fields the predicate reads; every one is a Submissions column. */
export interface SequenceSlotRow {
  readonly Status?: string | null;
  readonly EsgAckReceivedAt?: Date | string | null;
  readonly EsgAckPayload?: string | null;
}

/** Columns a caller must select for {@link isTechnicalRejection}. */
export const SEQUENCE_SLOT_COLUMNS = ['Status', 'EsgAckReceivedAt', 'EsgAckPayload'] as const;

/** True when a recorded ESG acknowledgement payload is the gateway's rejection shape. */
export function esgAckIsRejection(payload: string | null | undefined): boolean {
  if (!payload) return false;
  try {
    const parsed = JSON.parse(payload) as { kind?: unknown } | null;
    return parsed !== null && typeof parsed === 'object' && parsed.kind === 'rejected';
  } catch {
    return false;
  }
}

/**
 * True only for a sequence the gateway technically rejected (see the module
 * header). A bare 'Rejected' status, a 'Withdrawn' row, or an accepted ack is
 * not technical-rejection evidence.
 */
export function isTechnicalRejection(row: SequenceSlotRow): boolean {
  return row.Status === 'Rejected' && row.EsgAckReceivedAt != null && esgAckIsRejection(row.EsgAckPayload);
}

/** A row occupies its sequence number unless it carries technical-rejection evidence. */
export function occupiesSequenceSlot(row: SequenceSlotRow): boolean {
  return !isTechnicalRejection(row);
}
